import React, { useState } from 'react';
import { Card, Table, Button, Tag, Typography, Spin, Alert, Modal, Form, Input, Select, Switch, message } from 'antd';
import { PlusOutlined, EditOutlined, SettingOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAdminFeaturesQuery,
    useCreateAdminFeatureMutation,
    useUpdateAdminFeatureMutation,
    IFeatureDefinition,
    ICreateFeatureDto,
} from '../../services/nestjs-base-api';

const { Title } = Typography;
const { TextArea } = Input;

export const AdminFeaturesPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingFeature, setEditingFeature] = useState<IFeatureDefinition | null>(null);
    const [form] = Form.useForm();

    const { data: features, isLoading, error } = useGetAdminFeaturesQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [createFeature, { isLoading: isCreating }] = useCreateAdminFeatureMutation();
    const [updateFeature, { isLoading: isUpdating }] = useUpdateAdminFeatureMutation();

    const handleCreate = () => {
        setEditingFeature(null);
        form.resetFields();
        setIsCreateModalOpen(true);
    };

    const handleEdit = (feature: IFeatureDefinition) => {
        setEditingFeature(feature);
        form.setFieldsValue({
            code: feature.code,
            name: feature.name,
            description: feature.description,
            category: feature.category,
            featureType: feature.featureType,
            defaultValue: feature.defaultValue,
            isActive: feature.isActive,
        });
        setIsCreateModalOpen(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editingFeature) {
                await updateFeature({
                    jwtToken: user?.jwtToken || '',
                    id: editingFeature.id,
                    payload: values,
                }).unwrap();
                message.success(intl.formatMessage({ id: 'subscription.features.updateSuccess', defaultMessage: 'Feature updated successfully' }));
            } else {
                await createFeature({
                    jwtToken: user?.jwtToken || '',
                    payload: values as ICreateFeatureDto,
                }).unwrap();
                message.success(intl.formatMessage({ id: 'subscription.features.createSuccess', defaultMessage: 'Feature created successfully' }));
            }

            setIsCreateModalOpen(false);
            form.resetFields();
        } catch (error) {
            message.error(intl.formatMessage({
                id: editingFeature ? 'subscription.features.updateError' : 'subscription.features.createError',
                defaultMessage: editingFeature ? 'Failed to update feature' : 'Failed to create feature'
            }));
        }
    };

    const columns = [
        {
            title: <FormattedMessage id="subscription.features.code" defaultMessage="Code" />,
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: <FormattedMessage id="subscription.features.name" defaultMessage="Name" />,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: <FormattedMessage id="subscription.features.category" defaultMessage="Category" />,
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => (
                <Tag color="cyan">{category}</Tag>
            ),
        },
        {
            title: <FormattedMessage id="subscription.features.type" defaultMessage="Type" />,
            dataIndex: 'featureType',
            key: 'featureType',
            render: (type: string) => {
                const colors: Record<string, string> = {
                    boolean: 'blue',
                    limit: 'orange',
                    tier: 'purple',
                };
                return <Tag color={colors[type] || 'default'}>{type}</Tag>;
            },
        },
        {
            title: <FormattedMessage id="subscription.features.defaultValue" defaultMessage="Default Value" />,
            dataIndex: 'defaultValue',
            key: 'defaultValue',
        },
        {
            title: <FormattedMessage id="subscription.features.status" defaultMessage="Status" />,
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: <FormattedMessage id="common.actions" defaultMessage="Actions" />,
            key: 'actions',
            render: (_: any, record: IFeatureDefinition) => (
                <Button
                    size="small"
                    icon={<EditOutlined style={{ marginRight: 4 }} />}
                    onClick={() => handleEdit(record)}
                >
                    <FormattedMessage id="common.edit" defaultMessage="Edit" />
                </Button>
            ),
        },
    ];

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading features"
                    description="Unable to fetch features. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <SettingOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="subscription.features.title" defaultMessage="Features" />
                    </Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={handleCreate}
                    >
                        <FormattedMessage id="subscription.features.create" defaultMessage="Create Feature" />
                    </Button>
                </div>

                <Table
                    dataSource={features || []}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                />
            </Card>

            <Modal
                title={
                    editingFeature ? (
                        <FormattedMessage id="subscription.features.editTitle" defaultMessage="Edit Feature" />
                    ) : (
                        <FormattedMessage id="subscription.features.createTitle" defaultMessage="Create Feature" />
                    )
                }
                open={isCreateModalOpen}
                onOk={handleSubmit}
                onCancel={() => {
                    setIsCreateModalOpen(false);
                    form.resetFields();
                }}
                confirmLoading={isCreating || isUpdating}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="code"
                        label={<FormattedMessage id="subscription.features.code" defaultMessage="Code" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'subscription.features.codeRequired', defaultMessage: 'Code is required' }) }]}
                    >
                        <Input
                            placeholder={intl.formatMessage({ id: 'subscription.features.codePlaceholder', defaultMessage: 'e.g., max_monthly_responses' })}
                            disabled={!!editingFeature}
                        />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label={<FormattedMessage id="subscription.features.name" defaultMessage="Name" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'subscription.features.nameRequired', defaultMessage: 'Name is required' }) }]}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'subscription.features.namePlaceholder', defaultMessage: 'e.g., Monthly Response Limit' })} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={<FormattedMessage id="subscription.features.description" defaultMessage="Description" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'subscription.features.descriptionRequired', defaultMessage: 'Description is required' }) }]}
                    >
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label={<FormattedMessage id="subscription.features.category" defaultMessage="Category" />}
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Select.Option value="surveys">Surveys</Select.Option>
                            <Select.Option value="brands">Brands</Select.Option>
                            <Select.Option value="users">Users</Select.Option>
                            <Select.Option value="ai">AI</Select.Option>
                            <Select.Option value="analytics">Analytics</Select.Option>
                            <Select.Option value="benchmarking">Benchmarking</Select.Option>
                            <Select.Option value="integrations">Integrations</Select.Option>
                            <Select.Option value="support">Support</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="featureType"
                        label={<FormattedMessage id="subscription.features.type" defaultMessage="Type" />}
                        rules={[{ required: true }]}
                    >
                        <Select disabled={!!editingFeature}>
                            <Select.Option value="boolean">Boolean (On/Off)</Select.Option>
                            <Select.Option value="limit">Limit (Numeric)</Select.Option>
                            <Select.Option value="tier">Tier (Levels)</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="defaultValue"
                        label={<FormattedMessage id="subscription.features.defaultValue" defaultMessage="Default Value" />}
                        rules={[{ required: true }]}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'subscription.features.defaultValuePlaceholder', defaultMessage: 'e.g., false, 0, basic' })} />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label={<FormattedMessage id="subscription.features.isActive" defaultMessage="Active" />}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
